/**
 * Inline global search bar for the header.
 * Renders as a pill-shaped bar; on click/focus expands into a real input
 * with a dropdown results panel anchored directly below it.
 * Replaces the centered dialog modal on desktop.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useGlobalSearch } from '../../hooks/useGlobalSearch';
import { GlobalSearchResults, tagResultUrls } from './GlobalSearchResults';
import { GlobalSearchFilters } from './GlobalSearchFilters';
import { GlobalSearchFacets } from './GlobalSearchFacets';
import { cn } from '../../../lib/utils';

interface InlineGlobalSearchProps {
  /** Whether the search is open (controlled by parent for Cmd+K support) */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InlineGlobalSearch({ open, onOpenChange }: InlineGlobalSearchProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    query,
    setQuery,
    filters,
    setFilters,
    moduleResults,
    pageResults,
    suggestedPages,
    entities,
    tags,
    suggestions,
    facets,
    entityQuery,
    resultCount,
    isLoading,
  } = useGlobalSearch({ defaultModule: 'all' });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Reset state when search opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setFiltersOpen(false);
      // A category narrows the list to one kind of result, so carrying one over
      // into the next search would open the bar already hiding most of what it
      // is about to find.
      setFilters((prev) => ({
        ...prev,
        modules: ['all'],
        entityTypes: [],
      }));
      // Focus input with small delay
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open, setQuery, setFilters]);

  // Close on click outside the pill bar and dropdown
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inPill = pillRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      // The module multi-select (and any nested popover) portals its content
      // outside the dropdown subtree — treat clicks inside a Radix popper as
      // "inside" so selecting filters doesn't collapse the whole search.
      const inPopper =
        target instanceof Element &&
        target.closest('[data-radix-popper-content-wrapper]') !== null;
      if (!inPill && !inDropdown && !inPopper) {
        onOpenChange(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onOpenChange]);

  // Build flat list of all results for keyboard navigation.
  // Order must match GlobalSearchResults: modules → pages → tags → entities → suggestions.
  const allResults = useMemo(() => {
    const hasQuery = query.length >= 2 || moduleResults.length > 0 || pageResults.length > 0;
    if (hasQuery) {
      return [
        ...moduleResults.map((m) => m.url),
        ...pageResults.map((p) => p.url),
        ...tagResultUrls(tags),
        ...entities.map((e) => e.url),
        ...suggestions.map((e) => e.url),
      ];
    }
    // No query — suggestions are shown
    return suggestedPages.map((p) => p.url);
  }, [query, moduleResults, pageResults, suggestedPages, tags, entities, suggestions]);

  const totalResults = allResults.length;

  // Toggle an entity type in the facet filter.
  const handleToggleEntityType = useCallback(
    (entityType: string) => {
      setFilters((prev) => ({
        ...prev,
        entityTypes: prev.entityTypes.includes(entityType)
          ? prev.entityTypes.filter((et) => et !== entityType)
          : [...prev.entityTypes, entityType],
      }));
    },
    [setFilters],
  );

  // Navigate to selected result and close
  const handleSelect = useCallback(
    (url: string) => {
      onOpenChange(false);
      navigate(url);
    },
    [navigate, onOpenChange],
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % Math.max(totalResults, 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev <= 0 ? Math.max(totalResults - 1, 0) : prev - 1,
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (allResults[selectedIndex]) {
            handleSelect(allResults[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onOpenChange(false);
          inputRef.current?.blur();
          break;
      }
    },
    [totalResults, allResults, selectedIndex, handleSelect, onOpenChange],
  );

  // Reset the selection whenever the visible list changes shape. Choosing a
  // category is one of those moments and leaves `entityQuery.data` untouched —
  // the sentinels never reach the server, so the query key is unchanged and
  // React Query hands back the very same object. Without `allResults` here the
  // index survives a list it no longer fits: nothing renders as selected and
  // Enter silently does nothing.
  useEffect(() => {
    setSelectedIndex(0);
  }, [query, allResults]);

  const isMac = navigator.platform.toUpperCase().includes('MAC');
  const shortcutLabel = isMac ? '\u2318K' : 'Ctrl+K';

  return (
    <div className="relative flex-1 flex justify-center px-4" onKeyDown={open ? handleKeyDown : undefined}>
      {/* Pill bar — static when closed, becomes input when open */}
      <div
        ref={pillRef}
        className={cn(
          'relative flex items-center gap-2 h-9 rounded-full transition-all min-w-[220px] max-w-[400px] w-full',
          open
            ? 'bg-background ring-1 ring-ring shadow-sm'
            : 'bg-muted/60 hover:bg-muted cursor-text',
        )}
        role="search"
        aria-label={t('globalSearch.placeholder')}
        data-testid="global-search-trigger"
        onClick={() => {
          if (!open) onOpenChange(true);
        }}
      >
        <Search className="absolute left-3.5 h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        {open ? (
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('globalSearch.placeholder')}
            aria-label={t('globalSearch.placeholder')}
            data-testid="global-search-input"
            className="w-full h-full bg-transparent pl-10 pr-14 text-sm outline-none rounded-full"
          />
        ) : (
          <span className="pl-10 text-sm text-muted-foreground flex-1 text-left truncate" aria-hidden="true">
            {t('globalSearch.placeholder')}
          </span>
        )}
        <kbd className={cn(
          'absolute right-3 text-[10px] font-mono text-muted-foreground',
          'hidden lg:inline',
        )}
        aria-hidden="true">
          {shortcutLabel}
        </kbd>
      </div>

      {/* Dropdown results panel */}
      {open && (
        <div ref={dropdownRef} data-testid="global-search-dropdown" className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-full max-w-[560px] bg-background border rounded-xl shadow-lg overflow-hidden z-popover">
          <GlobalSearchFilters
            filters={filters}
            onFiltersChange={setFilters}
            isOpen={filtersOpen}
            onToggle={() => setFiltersOpen(!filtersOpen)}
          />
          <GlobalSearchFacets
            facets={query.length >= 2 ? facets : []}
            selected={filters.entityTypes}
            onToggle={handleToggleEntityType}
          />
          <GlobalSearchResults
            query={query}
            modules={moduleResults}
            pages={pageResults}
            suggestedPages={suggestedPages}
            entities={entities}
            tags={tags}
            suggestions={suggestions}
            matchTerms={entityQuery.data?.match_terms || []}
            isLoading={isLoading}
            selectedIndex={selectedIndex}
            onSelect={handleSelect}
          />

          {/* Footer */}
          <div className="border-t">
            {totalResults > 0 && (
              <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground">
                <span>
                  {query.length > 0 ? t('globalSearch.results', { count: resultCount }) : null}
                </span>
                <span>
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <kbd className="px-1 py-0.5 rounded border bg-muted font-mono text-[10px]">Enter</kbd>
                  {' '}{t('globalSearch.toOpen')}
                  {' '}<kbd className="px-1 py-0.5 rounded border bg-muted font-mono text-[10px]">&uarr;&darr;</kbd>
                  {' '}{t('globalSearch.toNavigate')}
                </span>
              </div>
            )}
            {/* Power-user query syntax hint (Tier 2) */}
            <div className="px-4 py-1.5 text-[11px] text-muted-foreground/70 truncate">
              {t('globalSearch.syntaxHint')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
